import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { getAllOrders } from "../../../api/action/order";
import Pagination from "../../../components/Pagination";
import OrderLayout from "./OrderLayout";
import { getRefundAwareOrderStatus } from "../../../utils/cancellationRefundStatus";

const Skeleton = () => <div className="my-2 h-8 w-full animate-pulse rounded bg-slate-200" />;

const Orders = () => {
  const dispatch = useDispatch();
  const userDetail = useSelector((state) => state?.auth?.data);
  const [orderDetailsArray, setOrderDetailsArray] = useState([]);
  const [isServer, setIsServer] = useState(false);
  const [accountId, setAccountId] = useState(null); // Initialize accountId to null
  const [sortByPriceAsc, setSortByPriceAsc] = useState(true); // Initialize sortByPriceAsc
  const [sortByRecentAsc, setSortByRecentAsc] = useState(true); // Initialize sortByRecentAsc
  const domainType = localStorage.getItem("domainType");

  useEffect(() => {
    // const userData = JSON.parse(localStorage.getItem("user"));
    const userData = userDetail;
    const accountId = userData?.account?.primaryId;
    setAccountId(accountId);
  }, [userDetail]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (accountId) {
          const fetchedOrders = await dispatch(getAllOrders(accountId));

          if (fetchedOrders.length > 0) {
            fetchedOrders.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));

            setOrderDetailsArray(fetchedOrders);
          }
        } else {
          setOrderDetailsArray([]);
        }
      } catch (error) {
        if (error.message === "Network Error") {
          setIsServer(true);
        }
      }
    };

    fetchOrders();
  }, [dispatch, accountId]);

  const cancelledOrders = useMemo(() => (
    orderDetailsArray.filter(order => getRefundAwareOrderStatus(order) === "CANCELLED")
  ), [orderDetailsArray]);

  const allOrders = useMemo(() => (
    orderDetailsArray.filter(order => getRefundAwareOrderStatus(order) !== "CANCELLED")
  ), [orderDetailsArray]);

  const toggleSortDirection = () => {
    const sortedOrders = [...orderDetailsArray];
    sortedOrders.sort((a, b) => {
      if (sortByPriceAsc) {
        return a.totalAmount - b.totalAmount;
      } else {
        return b.totalAmount - a.totalAmount;
      }
    });

    setOrderDetailsArray(sortedOrders);
    setSortByPriceAsc(prev => !prev);
    setSortByRecentAsc(true); // Reset sortByRecentAsc when toggling price sort
  };

  const toggleSortByRecent = () => {
    const sortedOrders = [...orderDetailsArray];
    sortedOrders.sort((a, b) => {
      if (sortByRecentAsc) {
        return new Date(b.createdDate) - new Date(a.createdDate);
      } else {
        return new Date(a.createdDate) - new Date(b.createdDate);
      }
    });

    setOrderDetailsArray(sortedOrders);
    setSortByRecentAsc(prev => !prev);
    setSortByPriceAsc(true); // Reset sortByPriceAsc when toggling recent sort
  };

  const [activeItem, setActiveItem] = useState("orders");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;
  const ordersToDisplay = activeItem === "cancelled orders" ? cancelledOrders : allOrders;
  const lastIndex = currentPage * ordersPerPage;
  const firstIndex = lastIndex - ordersPerPage;
  const currentOrders = ordersToDisplay.slice(firstIndex, lastIndex);
  const totalPages = Math.ceil(ordersToDisplay.length / ordersPerPage);
  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);

  const prePage = () => {
    if (currentPage !== 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleChangePage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const nextPage = () => {
    if (currentPage !== totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const menuList = [
    { id: 1, title: "orders", orders: allOrders },
    { id: 2, title: "cancelled orders", orders: cancelledOrders },
    { id: 3, title: "Sort by Recent", action: toggleSortByRecent },
  ];

  const handleMenuItemClick = (menuitem) => {
    setActiveItem(menuitem.title);
    if (menuitem.action) {
      menuitem.action();
    }
  };

  return (
    <div>
      {isServer ? (
        <div>
          <Skeleton />
          <Skeleton animation="wave" />
          <Skeleton animation={false} />
        </div>
      ) : (
        <div className="mx-auto max-w-7xl px-2 lg:px-0">
          {/* Search bar section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between capitalize">
            <div className="w-full md:w-1/2">
              <h2 className="text-3xl font-medium capitalize merriweather-bold-italic tracking-wide text-sky-800">
                {domainType === "RETAIL" ? "my orders" : "my enrollments"}
              </h2>
            </div>
            <div className="mt-10 w-full md:w-1/2 lg:mt-0">
              <form className="flex lg:justify-center">
                <div className="flex w-full max-w-md items-center space-x-2">
                  <input
                    className="flex h-10 w-full rounded-md border border-black/30 capitalize bg-transparent px-3 py-2 text-sm //placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-black/30 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                    type="text"
                  //placeholder="search orders"
                  />
                  <button
                    type="button"
                    className="rounded-md bg-cyan-800 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
                  >
                    {/* <ButtonGroup variant="text" color="default" size="small" aria-label="">
                    <Button></Button>
                    <Button></Button>
                    
                  </ButtonGroup> */}
                    Search
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Menus related section */}
          <div className="text-md font-medium text-center text-gray-600 border-b border-gray-200 dark:border-gray-700">
            <ul className="flex flex-wrap -mb-px capitalize">
              {menuList.map((menu) => (
                <li className="me-2" key={menu.id}>
                  <NavLink
                    to="#"
                    onClick={() => handleMenuItemClick(menu)}
                    className={`inline-block p-2 border-b-2 border-transparent rounded-t-lg ubuntu-bold
                      ${activeItem === menu.title
                        ? "border-blue-600 dark:text-blue-500 dark:border-blue-500"
                        : "hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300"
                      }`}
                  >
                    {menu.title}
                  </NavLink>
                </li>
              ))}
              {/* Sort by Price option */}
              <li className="me-2">
                <button
                  onClick={toggleSortDirection}
                  className="inline-block p-2 border-b-2 border-transparent rounded-t-lg ubuntu-bold hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300"
                >
                  Sort by Price {sortByPriceAsc ? "↑" : "↓"}
                </button>
              </li>
            </ul>
          </div>

          {/* Render your sorted orders */}
          <div>
            {currentOrders.length > 0 ? (
              currentOrders.map((orderItem) => (
                <OrderLayout
                  key={orderItem._id}
                  orderDetail={orderItem}
                />
              ))
            ) : (
              <div>
                <Skeleton />
                <Skeleton animation="wave" />
                <Skeleton animation={false} />
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="flex justify-center">
            {pageNumbers.length > 1 && (
              <Pagination
                pages={pageNumbers}
                currentPage={currentPage}
                prePage={prePage}
                nextPage={nextPage}
                changePage={handleChangePage}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
